import { useRouteLoaderData } from "@remix-run/react";
import { Info } from "lucide-react";
import type { MouseEvent, SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

// Strips tags/entities so tag-only HTML such as "<p></p>" counts as empty.
const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&(#039|#39|#8217|apos);/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

const cleanText = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  return value.trim() || null;
};

const cleanHtml = (value: unknown): string | null => {
  const text = cleanText(value);
  if (!text) return null;
  // Keep media-only HTML (e.g. a lone image) as non-empty.
  if (!stripHtml(text) && !/<(img|video|iframe)\b/i.test(text)) return null;
  return text;
};

/**
 * Subtitle and description of a service card. `code` null means the card
 * groups several codes, so the service-level fields are used. There is no
 * fallback between the two levels.
 */
export const getServiceCardTexts = (
  cmsService: any,
  code: string | null,
): { subtitle: string | null; description: string | null } => {
  if (code === null) {
    return {
      subtitle: cleanText(cmsService?.acf?.card_subtitle),
      description: cleanHtml(cmsService?.acf?.description),
    };
  }
  const codeObj = cmsService?.acf?.codes?.find((c: any) => c.code === code);
  return {
    subtitle: cleanText(codeObj?.card_subtitle),
    description: cleanHtml(codeObj?.long_description),
  };
};

export const ServiceCardSubtitle = ({ text }: { text?: string | null }) => {
  const value = cleanText(text);
  if (!value) return null;
  return <p className="text-sm text-gray-500 line-clamp-2">{value}</p>;
};

/**
 * Price a card shows for one service. Booking_Search prices a package per
 * unit of `NumPax` guests (a couple package costs the couple price), so a
 * package card shows that price divided by `NumPax`, i.e. per person. The
 * group total is only computed in the cart.
 */
export const getCardPrice = (service: any, price: number): number =>
  service?.Type === "Package" ? price / (service?.NumPax || 1) : price;

// ACF color picker value; anything else is ignored.
const cleanColor = (value: unknown): string | null => {
  const text = cleanText(value);
  return text && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(text) ? text : null;
};

/**
 * Line under the title of a service card, telling how the price reads. The
 * code's own `price_label` (WordPress, per language) wins on any card;
 * `code` null means a card grouping several codes, which has none. Without
 * it only a package card shows a line, since its price is per person: the
 * `package_price_label` of "App Settings", then ours. Its color is the
 * code's `price_label_color`, or the secondary one. A single guest sees no
 * line at all: per person and in total are the same price.
 */
export const ServicePriceLabel = ({
  cmsService,
  code,
  isPackage,
  totalGuests,
}: {
  cmsService: any;
  code: string | null;
  isPackage: boolean;
  totalGuests?: number;
}) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  if (totalGuests === 1) return null;
  const codeObj =
    code === null
      ? null
      : cmsService?.acf?.codes?.find((c: any) => c.code === code);
  const own = cleanText(codeObj?.price_label);
  if (!own && !isPackage) return null;
  const label =
    own ||
    cleanText(rootData?.footerData?.acf?.package_price_label) ||
    t("Prezzo per persona");
  const color = cleanColor(codeObj?.price_label_color);
  return (
    <span
      className="text-xs text-secondary mt-1"
      style={color ? { color } : undefined}
    >
      {label}
    </span>
  );
};

// ACF image returned as an attachment ID; an array or URL is ignored.
const cleanAssetId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * Attachment ID of the VIP icon a code's card shows, or null when it shows
 * none: the code is not flagged `featured` in WordPress, `code` is null (a
 * card grouping several codes) or "App Settings" has no `featured_icon`.
 */
export const getFeaturedIconId = (
  rootData: any,
  cmsService: any,
  code: string | null,
): number | null => {
  if (code === null) return null;
  const codeObj = cmsService?.acf?.codes?.find((c: any) => c.code === code);
  if (codeObj?.featured !== true) return null;
  return cleanAssetId(rootData?.footerData?.acf?.featured_icon);
};

/**
 * VIP icon of the card of a code flagged `featured` (see getFeaturedIconId),
 * served through /cms-asset so the CMS URL stays hidden.
 *
 * `onImage` puts it on a white tab with a slanted left edge and a rounded top
 * corner, meant to sit flush in the bottom-right corner of the card image and
 * join the white card body below. The caller positions it through
 * `className` (absolutely) inside a card that clips its overflow: the white
 * background runs past the tab's right edge. Without it the icon sits above
 * the title, for cards that have no image.
 */
export const FeaturedBadge = ({
  cmsService,
  code,
  onImage = false,
  className,
}: {
  cmsService: any;
  code: string | null;
  onImage?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const iconId = getFeaturedIconId(rootData, cmsService, code);
  if (!iconId) return null;
  const icon = (
    <img
      src={`/cms-asset/${iconId}`}
      alt={t("In evidenza")}
      className={onImage ? "block h-[1.2rem] w-auto" : "block h-5 w-auto self-start mb-1"}
    />
  );
  if (!onImage) return icon;
  return (
    <span className={cn("block pb-[5px] pl-[24px] pr-4 pt-[3px]", className)}>
      {/* Skewed from the bottom edge, so the left side slants at 30deg and
          its rounded top corner lands inside the left padding. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 -right-8 origin-bottom-left -skew-x-[30deg] rounded-tl-[0.2rem] bg-white"
      />
      <span className="relative block">{icon}</span>
    </span>
  );
};

interface ServiceInfoButtonProps {
  title?: string | null;
  description?: string | null;
  imageId?: string | number | null;
}

const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * Round info button for the top-left corner of a card (the card must be
 * `relative`) plus the modal it opens. Renders nothing without a description.
 */
export const ServiceInfoButton = ({
  title,
  description,
  imageId,
}: ServiceInfoButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const html = cleanHtml(description);
  if (!html) return null;

  const name = stripHtml(title || "");

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        onPointerDown={stop}
        onKeyDown={stop}
        aria-label={t("Maggiori informazioni su {{name}}", {
          name,
          // React escapes attributes itself; i18next would turn "'" into "&#39;".
          interpolation: { escapeValue: false },
        })}
        className="absolute top-2 left-2 z-40 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-secondary shadow-md transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
      >
        <Info size={18} />
      </button>
      {/*
        The dialog is portaled to <body>, but React synthetic events still
        bubble to the React ancestors of the Dialog, i.e. the clickable card.
        This wrapper (no layout of its own) swallows them before they get there.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        className="contents"
        onClick={stop}
        onPointerDown={stop}
        onKeyDown={stop}
      >
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              {imageId ? (
                <img
                  alt=""
                  src={`/cms-asset/${imageId}`}
                  className="mb-4 h-52 w-full rounded-xl object-cover object-center"
                />
              ) : null}
              <DialogTitle className="text-secondary font-bold text-xl leading-tight">
                {name}
              </DialogTitle>
            </DialogHeader>
            <div
              dangerouslySetInnerHTML={{ __html: html }}
              className="text-md text-gray-600"
            />
          </DialogContent>
        </Dialog>
      </span>
    </>
  );
};
